import React from 'react';
import * as MuiIcons from '@mui/icons-material';
import type { BoxNeighborSize, CellMode, CellShape } from '../../types';

/**
 * Get box border style for a cell based on its position within a box group.
 * Uses inset box-shadow to avoid conflicting with cell borders or Paper elevation.
 */
export const getBoxBorderStyle = (
  row: number,
  col: number,
  boxSize: BoxNeighborSize,
  borderColor: string,
  borderWidth: number
): React.CSSProperties => {
  let isTopEdge: boolean;
  let isBottomEdge: boolean;
  let isLeftEdge: boolean;
  let isRightEdge: boolean;

  if (boxSize === '2x2') {
    // 2x2 boxes: divide into 5x5 grid of boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 2 === 0;
    isRightEdge = col % 2 === 1;
  } else if (boxSize === '5x5') {
    // 5x5 quadrants: 4 quadrants (top-left, top-right, bottom-left, bottom-right)
    isTopEdge = row % 5 === 0;
    isBottomEdge = row % 5 === 4;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  } else {
    // 5x2 boxes: 2 columns of 5-wide boxes, 5 rows of 2-tall boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  }

  // Build inset box-shadow for each edge that needs a border
  // Using box-shadow avoids conflicts with existing cell borders
  const shadows: string[] = [];
  if (isTopEdge) {
    shadows.push(`inset 0 ${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isBottomEdge) {
    shadows.push(`inset 0 -${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isLeftEdge) {
    shadows.push(`inset ${borderWidth}px 0 0 0 ${borderColor}`);
  }
  if (isRightEdge) {
    shadows.push(`inset -${borderWidth}px 0 0 0 ${borderColor}`);
  }

  if (shadows.length === 0) {
    return {};
  }

  return {
    boxShadow: shadows.join(', '),
  };
};

/**
 * Get contrasting text color based on background.
 * Returns black for light backgrounds and white for dark backgrounds.
 */
export const getContrastColor = (bgColor: string): string => {
  // Remove # if present
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Render a Material-UI icon by name.
 */
export const renderIcon = (name: string, size: number = 18): React.ReactNode => {
  const IconComponent = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

/**
 * Grid dimensions for merged cell rendering.
 */
export interface GridDimensions {
  rows: number;
  cols: number;
  rowSpan: number;
  colSpan: number;
}

/**
 * Get grid dimensions for merged cell rendering.
 * Returns { rows, cols, rowSpan, colSpan } for the visual grid.
 */
export const getMergedGridDimensions = (cellMode: CellMode, cellShape: CellShape): GridDimensions => {
  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: 5 visual rows x 10 cols, each cell spans 2 rows
      return { rows: 5, cols: 10, rowSpan: 2, colSpan: 1 };
    } else {
      // Horizontal pairs (1x2): 10 visual rows x 5 cols, each cell spans 2 cols
      return { rows: 10, cols: 5, rowSpan: 1, colSpan: 2 };
    }
  } else if (cellMode === 'quad') {
    // 2x2 quads: 5 visual rows x 5 cols, each cell spans 2 rows and 2 cols
    return { rows: 5, cols: 5, rowSpan: 2, colSpan: 2 };
  }
  // Standard mode: 10x10, no spanning
  return { rows: 10, cols: 10, rowSpan: 1, colSpan: 1 };
};

/**
 * Get the cell number for a visual grid position in merged mode.
 */
export const getCellNumberForVisualPosition = (
  visualRow: number,
  visualCol: number,
  cellMode: CellMode,
  cellShape: CellShape
): number => {
  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: 5 rows x 10 cols = 50 cells, numbered left-to-right, top-to-bottom
      return visualRow * 10 + visualCol + 1;
    } else {
      // Horizontal pairs: 10 rows x 5 cols = 50 cells
      return visualRow * 5 + visualCol + 1;
    }
  } else if (cellMode === 'quad') {
    // Quad: 5 rows x 5 cols = 25 cells
    return visualRow * 5 + visualCol + 1;
  }
  // Standard: 10 rows x 10 cols = 100 squares
  return visualRow * 10 + visualCol + 1;
};

/**
 * Grid layout constants.
 */
export const GRID_CONSTANTS = {
  cellSize: 50,
  headerSize: 40,
  gap: 4,
} as const;
